import { lazy, Suspense, useState } from 'react';

import { Elements } from '@stripe/react-stripe-js';
import { Route, Switch, useParams } from 'react-router-dom';

import { ExternalRedirect } from '@m/components/ExternalRedirect';
import { getCustomerAppUrl } from '@m/utils/getCustomerAppUrl';
import { stripePromise } from '@m/utils/stripeLoader';
import { Page } from 'apps/pay/components';
import { useVisitDetails } from 'apps/pay/hooks/payment';
import VisitLandingPage from 'apps/pay/screens/VisitLandingPage';

const VisitCompletePage = lazy(() => import('apps/pay/screens/VisitCompletePage'));
const VisitReceiptPage = lazy(() => import('apps/pay/screens/VisitReceiptPage'));

// Shared wrapper for visit-based pages with Stripe Elements
function StripeElementsVisitPage({
  PageComponent,
  pageProps = {},
  remountOnSecretChange = false,
}: {
  PageComponent: React.ComponentType<any>;
  pageProps?: Record<string, any>;
  /**
   * Stripe Elements treats `options.clientSecret` as immutable: a new secret passed to a mounted
   * `<Elements>` is ignored (it only logs "options.clientSecret is not a mutable property"), so the
   * Payment Element would keep confirming the PaymentIntent from the first load while the page
   * shows the price from the refetched visit. Keying on the secret remounts Elements, and the
   * Payment Element and wallet buttons beneath it, when the visit query returns a new one.
   *
   * The remount also resets the page's local state, so the page can hold the current secret (via
   * `onHoldClientSecretChange`) while a pay attempt is in flight or a payment outcome is shown; a
   * new secret is adopted once it releases. A secret that disappears (the visit was paid) is never
   * adopted: the page keeps its state and the closed-visit redirect takes over.
   */
  remountOnSecretChange?: boolean;
}) {
  const { visitCode } = useParams<{ visitCode: string }>();
  const { data: visit, isPending } = useVisitDetails(visitCode);

  const [isClientSecretHeld, setIsClientSecretHeld] = useState(false);
  const [adoptedClientSecret, setAdoptedClientSecret] = useState(visit?.clientSecret);
  const latestClientSecret = visit?.clientSecret;
  if (
    remountOnSecretChange &&
    !isClientSecretHeld &&
    latestClientSecret &&
    latestClientSecret !== adoptedClientSecret
  ) {
    setAdoptedClientSecret(latestClientSecret);
  }

  if (isPending) {
    return 'Loading...';
  }

  // A failed background refetch keeps the last visit; keep rendering it so the page (and any
  // terminal payment outcome it holds) stays mounted. Only a visit that never loaded is fatal.
  if (visit === undefined) {
    return 'No data';
  }

  const clientSecret = remountOnSecretChange ? adoptedClientSecret : visit.clientSecret;
  const stripeOpts = clientSecret ? { clientSecret } : undefined;

  return (
    <Elements
      key={remountOnSecretChange ? clientSecret : undefined}
      stripe={stripePromise}
      options={stripeOpts}
    >
      <PageComponent
        visit={visit}
        visitCode={visitCode}
        usePaymentElement={!!stripeOpts}
        {...(remountOnSecretChange && { onHoldClientSecretChange: setIsClientSecretHeld })}
        {...pageProps}
      />
    </Elements>
  );
}

export default function Pay() {
  return (
    <Page paddingBottom="24px">
      <Switch>
        <Route exact path="/">
          <ExternalRedirect to={getCustomerAppUrl()} />
        </Route>
        <Route exact path="/:visitCode">
          <StripeElementsVisitPage PageComponent={VisitLandingPage} remountOnSecretChange />
        </Route>
        {/* Exit route if payment completed */}
        <Route exact path="/:visitCode/complete">
          <Suspense fallback={null}>
            <StripeElementsVisitPage PageComponent={VisitCompletePage} />
          </Suspense>
        </Route>
        {/* Exit route if "not my vehicle" */}
        <Route exact path="/:visitCode/drive-out">
          <Suspense fallback={null}>
            <StripeElementsVisitPage
              PageComponent={VisitCompletePage}
              pageProps={{ hasReceipt: false }}
            />
          </Suspense>
        </Route>
        <Route exact path="/:visitCode/receipt">
          <Suspense fallback={null}>
            <VisitReceiptPage />
          </Suspense>
        </Route>
      </Switch>
    </Page>
  );
}
